import React from 'react';
import {
    DefaultButton,
    Image,
    ImageFit,
    MessageBar,
    MessageBarType,
    PanelType,
    SearchBox,
    Shimmer,
    Stack,
    useTheme,
} from '@fluentui/react';
import PanelWrapper from '../../Common/PanelWrapper';
import { IStorefrontAsset, STOREFRONT_ASSET_GLOBAL } from '../../../models/Storefront';
import { useGetStorefrontAssetsQuery } from '../../../store/endpoints/StorefrontAssetEndpoints';

type AssetScope = 'storefront' | 'global' | 'all';

interface IAssetPickerPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    onSelect: (asset: IStorefrontAsset) => void;
    // Scope of the screen that opened the picker; 'This storefront' filters on it. May be
    // STOREFRONT_ASSET_GLOBAL, in which case that filter is redundant and hidden.
    storefrontId: string;
}

const CARD_WIDTH = 150;
const THUMB_HEIGHT = 110;

const AssetPickerPanel: React.FC<IAssetPickerPanelProps> = (props) => {
    const theme = useTheme();
    // Everything visible to the org; the scope pills filter client-side so switching between them
    // is instant and doesn't refetch.
    const assets = useGetStorefrontAssetsQuery(undefined, { skip: !props.isOpen });
    const [search, setSearch] = React.useState('');
    const [scope, setScope] = React.useState<AssetScope>('all');

    const isGlobalScreen = props.storefrontId === STOREFRONT_ASSET_GLOBAL;

    const filtered = React.useMemo(() => {
        const term = search.trim().toLowerCase();
        return (assets.data?.items ?? []).filter(a => {
            if (scope === 'storefront' && a.storefront !== props.storefrontId) return false;
            if (scope === 'global' && a.storefront !== STOREFRONT_ASSET_GLOBAL) return false;
            if (!term) return true;
            return (a.name ?? '').toLowerCase().includes(term)
                || (a.tags ?? []).some(t => t.toLowerCase().includes(term));
        });
    }, [assets.data, search, scope, props.storefrontId]);

    const scopeButton = (key: AssetScope, text: string) => (
        <DefaultButton
            key={key}
            text={text}
            toggle
            checked={scope === key}
            onClick={() => setScope(key)}
        />
    );

    return (
        <PanelWrapper
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            title='Select Asset'
            type={PanelType.medium}
        >
            <Stack tokens={{ childrenGap: '1em' }} style={{ paddingTop: '1em' }}>
                <SearchBox
                    placeholder='Search by name or tag'
                    value={search}
                    onChange={(_, v) => setSearch(v ?? '')}
                    onClear={() => setSearch('')}
                />
                <Stack horizontal tokens={{ childrenGap: '0.5em' }}>
                    {!isGlobalScreen && scopeButton('storefront', 'This storefront')}
                    {scopeButton('global', 'Global')}
                    {scopeButton('all', 'All')}
                </Stack>
                {assets.isError && (
                    <MessageBar messageBarType={MessageBarType.error}>Failed to load assets.</MessageBar>
                )}
                <Shimmer isDataLoaded={!assets.isLoading}>
                    {filtered.length === 0 && !assets.isLoading && (
                        <MessageBar>
                            {search || scope !== 'all'
                                ? 'No assets match this filter.'
                                : 'No assets yet. Upload an image to start the library.'}
                        </MessageBar>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1em' }}>
                        {filtered.map(asset => (
                            <div
                                key={asset.id}
                                onClick={() => props.onSelect(asset)}
                                title={asset.name}
                                style={{
                                    width: CARD_WIDTH,
                                    cursor: 'pointer',
                                    border: `1px solid ${theme.palette.neutralLight}`,
                                    borderRadius: 2,
                                    padding: '0.5em',
                                }}
                            >
                                <div style={{
                                    height: THUMB_HEIGHT,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.neutralLighterAlt,
                                }}>
                                    <Image
                                        src={asset.url}
                                        alt={asset.name}
                                        width={CARD_WIDTH - 18}
                                        height={THUMB_HEIGHT}
                                        imageFit={ImageFit.contain}
                                    />
                                </div>
                                <div style={{
                                    marginTop: '0.5em',
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {asset.name}
                                </div>
                                {asset.storefront === STOREFRONT_ASSET_GLOBAL && (
                                    <div style={{ fontSize: 11, color: theme.palette.themePrimary }}>Global</div>
                                )}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                                    {(asset.tags ?? []).map(tag => (
                                        <span
                                            key={tag}
                                            style={{
                                                fontSize: 11,
                                                padding: '1px 6px',
                                                borderRadius: 10,
                                                backgroundColor: theme.palette.themeLighter,
                                                color: theme.palette.themeDarker,
                                            }}
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </Shimmer>
            </Stack>
        </PanelWrapper>
    );
};

export default AssetPickerPanel;
